import { unwrapPositive, validPositive } from './std';


import { BigNatural, CommitSha, FilePath, LineNumber, unwrapLineNumber, validBigNatural } from './runtime';


export type AuthHook = "authError" | "authCard"

export const validAuthHook = (x: unknown): AuthHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'authError')) && ((v != 'authCard'))) throw new Error('Invalid AuthHook `' + JSON.stringify(x) + '`');
  return x as AuthHook;
}

export type AuthElementId = "authEmail" | "authPassword" | "authUsername" | "authDisplayName" | "authError" | "authSubmit"

export const validAuthElementId = (x: unknown): AuthElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'authEmail')) && ((v != 'authPassword')) && ((v != 'authUsername')) && ((v != 'authDisplayName')) && ((v != 'authError')) && ((v != 'authSubmit'))) throw new Error('Invalid AuthElementId `' + JSON.stringify(x) + '`');
  return x as AuthElementId;
}

export type UiHook = "modal" | "toast" | "dropdown" | "clipboard" | "tableSortAnimate" | "detailsFold" | "keyboardShortcuts" | "rowExpander" | "commitPanelToggle" | "fileExpander" | "deleteConfirmGate" | "workspacesNavToggle" | "clientTableSort" | "clientListFilter" | "disclosureToggle" | "sectionToggle" | "profileDropdown" | "branchSelect"

export const validUiHook = (x: unknown): UiHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'modal')) && ((v != 'toast')) && ((v != 'dropdown')) && ((v != 'clipboard')) && ((v != 'tableSortAnimate')) && ((v != 'detailsFold')) && ((v != 'keyboardShortcuts')) && ((v != 'rowExpander')) && ((v != 'commitPanelToggle')) && ((v != 'fileExpander')) && ((v != 'deleteConfirmGate')) && ((v != 'workspacesNavToggle')) && ((v != 'clientTableSort')) && ((v != 'clientListFilter')) && ((v != 'disclosureToggle')) && ((v != 'sectionToggle')) && ((v != 'profileDropdown')) && ((v != 'branchSelect'))) throw new Error('Invalid UiHook `' + JSON.stringify(x) + '`');
  return x as UiHook;
}

export type BranchSelectPushEvent = "branchSelectToggle" | "branchSelectData" | "branchSelectChoose" | "branchLaneCreate"

export const validBranchSelectPushEvent = (x: unknown): BranchSelectPushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'branchSelectToggle')) && ((v != 'branchSelectData')) && ((v != 'branchSelectChoose')) && ((v != 'branchLaneCreate'))) throw new Error('Invalid BranchSelectPushEvent `' + JSON.stringify(x) + '`');
  return x as BranchSelectPushEvent;
}

export type ToastElementId = "toastContainer" | "toastInfo" | "toastError"

export const validToastElementId = (x: unknown): ToastElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'toastContainer')) && ((v != 'toastInfo')) && ((v != 'toastError'))) throw new Error('Invalid ToastElementId `' + JSON.stringify(x) + '`');
  return x as ToastElementId;
}

export type ToastKind = "info" | "warning" | "error"

export const validToastKind = (x: unknown): ToastKind => {
  const v = x as [string, unknown] | string;
  if(((v != 'info')) && ((v != 'warning')) && ((v != 'error'))) throw new Error('Invalid ToastKind `' + JSON.stringify(x) + '`');
  return x as ToastKind;
}

export interface ToastAction {
  label: string;
  href: string;
}

export const validToastAction = (input: unknown): ToastAction => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["label"] === undefined) throw new Error('`_x["label"]` required but not present');
  if (typeof _x["label"] != 'string') throw new Error('`_x["label"]` must be a string');
  if (_x["href"] === undefined) throw new Error('`_x["href"]` required but not present');
  if (typeof _x["href"] != 'string') throw new Error('`_x["href"]` must be a string');
  const x = input as ToastAction;
  return x;
}
export const isValidToastAction = (input: unknown): boolean => {
  try { validToastAction(input); return true; } catch { return false; }
}

export interface ToastEventDetail {
  kind: ToastKind;
  message: string;
  id: string;
  action: ToastAction | null;
}

export const validToastEventDetail = (input: unknown): ToastEventDetail => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["kind"] === undefined) throw new Error('`_x["kind"]` required but not present');
  validToastKind(_x["kind"] as any);
  if (_x["message"] === undefined) throw new Error('`_x["message"]` required but not present');
  if (typeof _x["message"] != 'string') throw new Error('`_x["message"]` must be a string');
  if (_x["id"] === undefined) throw new Error('`_x["id"]` required but not present');
  if (typeof _x["id"] != 'string') throw new Error('`_x["id"]` must be a string');
  if (_x["action"] === undefined) throw new Error('`_x["action"]` required but not present');
  _x["action"] === null || validToastAction(_x["action"] as any);
  const x = input as ToastEventDetail;
  return x;
}
export const isValidToastEventDetail = (input: unknown): boolean => {
  try { validToastEventDetail(input); return true; } catch { return false; }
}

export type ChannelEvent = "logLine" | "logChunk" | "jobStatus"

export const validChannelEvent = (x: unknown): ChannelEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'logLine')) && ((v != 'logChunk')) && ((v != 'jobStatus'))) throw new Error('Invalid ChannelEvent `' + JSON.stringify(x) + '`');
  return x as ChannelEvent;
}

export type DomEvent = "phxCopy" | "phxSetViewTab" | "phxToast" | "phxServerDraining" | "phxSaveFile" | "phxEditorDocument"

export const validDomEvent = (x: unknown): DomEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'phxCopy')) && ((v != 'phxSetViewTab')) && ((v != 'phxToast')) && ((v != 'phxServerDraining')) && ((v != 'phxSaveFile')) && ((v != 'phxEditorDocument'))) throw new Error('Invalid DomEvent `' + JSON.stringify(x) + '`');
  return x as DomEvent;
}

export type CookieKey = "viewTab"

export const validCookieKey = (x: unknown): CookieKey => {
  const v = x as [string, unknown] | string;
  if(((v != 'viewTab'))) throw new Error('Invalid CookieKey `' + JSON.stringify(x) + '`');
  return x as CookieKey;
}

export type ModalCloseEvent = "cancelAvatar" | "cancelEditing" | "closeAttachEnvHostModal" | "closeAttachRunnerModal" | "closeDeleteSubgroupModal" | "closeGroupDetailsModal" | "closeInviteModal" | "closeMoveGroupModal" | "closeMoveRepoModal" | "closeMoveReposModal" | "closeNewGroupModal" | "closeNewLaneModal" | "closeNewPrModal" | "closeNewRepoModal" | "closePbModal" | "closePtModal" | "closeSubgroupModal" | "closeWorkspacePickerModal" | "toggleClone" | "toggleFork"

export const validModalCloseEvent = (x: unknown): ModalCloseEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'cancelAvatar')) && ((v != 'cancelEditing')) && ((v != 'closeAttachEnvHostModal')) && ((v != 'closeAttachRunnerModal')) && ((v != 'closeDeleteSubgroupModal')) && ((v != 'closeGroupDetailsModal')) && ((v != 'closeInviteModal')) && ((v != 'closeMoveGroupModal')) && ((v != 'closeMoveRepoModal')) && ((v != 'closeMoveReposModal')) && ((v != 'closeNewGroupModal')) && ((v != 'closeNewLaneModal')) && ((v != 'closeNewPrModal')) && ((v != 'closeNewRepoModal')) && ((v != 'closePbModal')) && ((v != 'closePtModal')) && ((v != 'closeSubgroupModal')) && ((v != 'closeWorkspacePickerModal')) && ((v != 'toggleClone')) && ((v != 'toggleFork'))) throw new Error('Invalid ModalCloseEvent `' + JSON.stringify(x) + '`');
  return x as ModalCloseEvent;
}

export type SortDir = "asc" | "desc"

export const validSortDir = (x: unknown): SortDir => {
  const v = x as [string, unknown] | string;
  if(((v != 'asc')) && ((v != 'desc'))) throw new Error('Invalid SortDir `' + JSON.stringify(x) + '`');
  return x as SortDir;
}

export type CiHook = "dagGraph" | "buildLog" | "evalLog" | "sparkline" | "ciAnimations" | "kdeChart" | "scatterPlot" | "jobDetailPanel" | "pipelineFavicon" | "jobMetricsChart" | "runnerMetricsChart" | "sessionMetricsChart" | "liveDuration" | "recentCommits" | "commitSelect"

export const validCiHook = (x: unknown): CiHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'dagGraph')) && ((v != 'buildLog')) && ((v != 'evalLog')) && ((v != 'sparkline')) && ((v != 'ciAnimations')) && ((v != 'kdeChart')) && ((v != 'scatterPlot')) && ((v != 'jobDetailPanel')) && ((v != 'pipelineFavicon')) && ((v != 'jobMetricsChart')) && ((v != 'runnerMetricsChart')) && ((v != 'sessionMetricsChart')) && ((v != 'liveDuration')) && ((v != 'recentCommits')) && ((v != 'commitSelect'))) throw new Error('Invalid CiHook `' + JSON.stringify(x) + '`');
  return x as CiHook;
}

export type Surface = "canvas" | "svg"

export const validSurface = (x: unknown): Surface => {
  const v = x as [string, unknown] | string;
  if(((v != 'canvas')) && ((v != 'svg'))) throw new Error('Invalid Surface `' + JSON.stringify(x) + '`');
  return x as Surface;
}

export type DataAttr = "jobId" | "runnerId" | "sessionId" | "cores" | "ramBytes" | "initialSamples" | "statusFilter" | "chartType" | "color" | "unit" | "eventKey" | "estimatedMs" | "buildStartedAt" | "streaming" | "allHashes" | "selected" | "historyWaypoint" | "rowSelector" | "armRootSelector" | "armChangeSelector" | "editorMode"

export const validDataAttr = (x: unknown): DataAttr => {
  const v = x as [string, unknown] | string;
  if(((v != 'jobId')) && ((v != 'runnerId')) && ((v != 'sessionId')) && ((v != 'cores')) && ((v != 'ramBytes')) && ((v != 'initialSamples')) && ((v != 'statusFilter')) && ((v != 'chartType')) && ((v != 'color')) && ((v != 'unit')) && ((v != 'eventKey')) && ((v != 'estimatedMs')) && ((v != 'buildStartedAt')) && ((v != 'streaming')) && ((v != 'allHashes')) && ((v != 'selected')) && ((v != 'historyWaypoint')) && ((v != 'rowSelector')) && ((v != 'armRootSelector')) && ((v != 'armChangeSelector')) && ((v != 'editorMode'))) throw new Error('Invalid DataAttr `' + JSON.stringify(x) + '`');
  return x as DataAttr;
}

export interface CiHookDomContract {
  hook: CiHook;
  surface: Surface | null;
  requiredDataAttrs: DataAttr[];
}

export const validCiHookDomContract = (input: unknown): CiHookDomContract => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["hook"] === undefined) throw new Error('`_x["hook"]` required but not present');
  validCiHook(_x["hook"] as any);
  if (_x["surface"] === undefined) throw new Error('`_x["surface"]` required but not present');
  _x["surface"] === null || validSurface(_x["surface"] as any);
  if (_x["requiredDataAttrs"] === undefined) throw new Error('`_x["requiredDataAttrs"]` required but not present');
    if(_x["requiredDataAttrs"] instanceof Array){
    if((_x["requiredDataAttrs"] as any)?.length){
      (_x["requiredDataAttrs"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validDataAttr(y as any);
      });
    }else{
      _x["requiredDataAttrs"] = _x["requiredDataAttrs"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["requiredDataAttrs"]` must be an Array');
  }
  const x = input as CiHookDomContract;
  return x;
}
export const isValidCiHookDomContract = (input: unknown): boolean => {
  try { validCiHookDomContract(input); return true; } catch { return false; }
}

export type CiElementId = "pipelineHealth" | "pipelineSparklines" | "pipelineList" | "pipelineGraph" | "pipelineGraphCanvas" | "jobDetailPanel" | "buildLogTerminal" | "buildLogSearch" | "buildLogFullscreen" | "buildLogJumpBottom" | "evalDetailPanel" | "flakeInputsPanel" | "pipelineFilterBar" | "jobFilterSystem" | "jobFilterStatus" | "jobFilterSearch" | "followModeToggle" | "pipelineRetryBtn" | "pipelineCancelBtn"

export const validCiElementId = (x: unknown): CiElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'pipelineHealth')) && ((v != 'pipelineSparklines')) && ((v != 'pipelineList')) && ((v != 'pipelineGraph')) && ((v != 'pipelineGraphCanvas')) && ((v != 'jobDetailPanel')) && ((v != 'buildLogTerminal')) && ((v != 'buildLogSearch')) && ((v != 'buildLogFullscreen')) && ((v != 'buildLogJumpBottom')) && ((v != 'evalDetailPanel')) && ((v != 'flakeInputsPanel')) && ((v != 'pipelineFilterBar')) && ((v != 'jobFilterSystem')) && ((v != 'jobFilterStatus')) && ((v != 'jobFilterSearch')) && ((v != 'followModeToggle')) && ((v != 'pipelineRetryBtn')) && ((v != 'pipelineCancelBtn'))) throw new Error('Invalid CiElementId `' + JSON.stringify(x) + '`');
  return x as CiElementId;
}

export type CiPushEvent = "sparklineUpdate" | "graphUpdate" | "graphState" | "buildLogLine" | "buildLogClear" | "buildLogLoad" | "builderStarted" | "jobSelect" | "jobDeselect" | "attemptSelect" | "jobMetricsSample" | "jobMetricsLoad" | "runnerMetricsSample" | "runnerMetricsLoad" | "sessionMetricsSample" | "evalLogLine" | "evalLogClear"

export const validCiPushEvent = (x: unknown): CiPushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'sparklineUpdate')) && ((v != 'graphUpdate')) && ((v != 'graphState')) && ((v != 'buildLogLine')) && ((v != 'buildLogClear')) && ((v != 'buildLogLoad')) && ((v != 'builderStarted')) && ((v != 'jobSelect')) && ((v != 'jobDeselect')) && ((v != 'attemptSelect')) && ((v != 'jobMetricsSample')) && ((v != 'jobMetricsLoad')) && ((v != 'runnerMetricsSample')) && ((v != 'runnerMetricsLoad')) && ((v != 'sessionMetricsSample')) && ((v != 'evalLogLine')) && ((v != 'evalLogClear'))) throw new Error('Invalid CiPushEvent `' + JSON.stringify(x) + '`');
  return x as CiPushEvent;
}

export type CssClassDiff = "diffCommentBtn" | "diffCommentBtnReply" | "diffInlineWrap" | "diffInlineCell" | "diffInlineFormClient" | "inlineCommentForm"

export const validCssClassDiff = (x: unknown): CssClassDiff => {
  const v = x as [string, unknown] | string;
  if(((v != 'diffCommentBtn')) && ((v != 'diffCommentBtnReply')) && ((v != 'diffInlineWrap')) && ((v != 'diffInlineCell')) && ((v != 'diffInlineFormClient')) && ((v != 'inlineCommentForm'))) throw new Error('Invalid CssClassDiff `' + JSON.stringify(x) + '`');
  return x as CssClassDiff;
}

export type CssClassCodeBrowser = "blameActive" | "blameDivider" | "cmBlameGutter" | "cmBlameAnnotation" | "cmCommentedLine" | "cmHighlightedLine" | "codeSidebar" | "codeEditorLayout" | "dragging" | "resizing" | "sidebarHidden" | "sidebarCloseBtn" | "workspaceTerminalFullscreen"

export const validCssClassCodeBrowser = (x: unknown): CssClassCodeBrowser => {
  const v = x as [string, unknown] | string;
  if(((v != 'blameActive')) && ((v != 'blameDivider')) && ((v != 'cmBlameGutter')) && ((v != 'cmBlameAnnotation')) && ((v != 'cmCommentedLine')) && ((v != 'cmHighlightedLine')) && ((v != 'codeSidebar')) && ((v != 'codeEditorLayout')) && ((v != 'dragging')) && ((v != 'resizing')) && ((v != 'sidebarHidden')) && ((v != 'sidebarCloseBtn')) && ((v != 'workspaceTerminalFullscreen'))) throw new Error('Invalid CssClassCodeBrowser `' + JSON.stringify(x) + '`');
  return x as CssClassCodeBrowser;
}

export type CssClassModal = "modalPanel" | "modalBackdrop" | "awaitingGsap" | "fadeOut" | "appShell"

export const validCssClassModal = (x: unknown): CssClassModal => {
  const v = x as [string, unknown] | string;
  if(((v != 'modalPanel')) && ((v != 'modalBackdrop')) && ((v != 'awaitingGsap')) && ((v != 'fadeOut')) && ((v != 'appShell'))) throw new Error('Invalid CssClassModal `' + JSON.stringify(x) + '`');
  return x as CssClassModal;
}

export type CssClassProfile = "profileAvatar" | "profileName" | "profileMeta" | "profileSocials"

export const validCssClassProfile = (x: unknown): CssClassProfile => {
  const v = x as [string, unknown] | string;
  if(((v != 'profileAvatar')) && ((v != 'profileName')) && ((v != 'profileMeta')) && ((v != 'profileSocials'))) throw new Error('Invalid CssClassProfile `' + JSON.stringify(x) + '`');
  return x as CssClassProfile;
}

export type EditorMode = "source" | "vim" | "rich" | "rendered"

export const validEditorMode = (x: unknown): EditorMode => {
  const v = x as [string, unknown] | string;
  if(((v != 'source')) && ((v != 'vim')) && ((v != 'rich')) && ((v != 'rendered'))) throw new Error('Invalid EditorMode `' + JSON.stringify(x) + '`');
  return x as EditorMode;
}

export type EditorAction = "toSource" | "toVim" | "toRich" | "toRendered"

export const validEditorAction = (x: unknown): EditorAction => {
  const v = x as [string, unknown] | string;
  if(((v != 'toSource')) && ((v != 'toVim')) && ((v != 'toRich')) && ((v != 'toRendered'))) throw new Error('Invalid EditorAction `' + JSON.stringify(x) + '`');
  return x as EditorAction;
}

export type EditorHook = "codeEditor" | "historyViewer" | "readmeHighlight" | "diffHighlight" | "diffInlineWrap" | "diffCommentBtn" | "codeRefAutocomplete" | "markdownEditor" | "vimToggle" | "editorModeButton" | "paneDivider" | "scrollPreserve" | "workspaceTerminal" | "commentAnchor"

export const validEditorHook = (x: unknown): EditorHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'codeEditor')) && ((v != 'historyViewer')) && ((v != 'readmeHighlight')) && ((v != 'diffHighlight')) && ((v != 'diffInlineWrap')) && ((v != 'diffCommentBtn')) && ((v != 'codeRefAutocomplete')) && ((v != 'markdownEditor')) && ((v != 'vimToggle')) && ((v != 'editorModeButton')) && ((v != 'paneDivider')) && ((v != 'scrollPreserve')) && ((v != 'workspaceTerminal')) && ((v != 'commentAnchor'))) throw new Error('Invalid EditorHook `' + JSON.stringify(x) + '`');
  return x as EditorHook;
}

export type EditorElementId = "readmeContent" | "codeEditorLayout" | "shortcutsOverlay" | "outlinePanel" | "paneDivider" | "historyViewer" | "historyScroll" | "vimToggle" | "easymdeToggle" | "renderedToggle" | "easymdeContainer" | "richEditor" | "renderedContent" | "codeViewer" | "commentsPanel" | "commentDraftForm" | "workspacePanel" | "workspaceTerminal" | "workspaceShellSelect" | "workspaceShellForm" | "workspaceFullscreenToggle" | "workspacePanelDivider" | "workspacePanelToggle"

export const validEditorElementId = (x: unknown): EditorElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'readmeContent')) && ((v != 'codeEditorLayout')) && ((v != 'shortcutsOverlay')) && ((v != 'outlinePanel')) && ((v != 'paneDivider')) && ((v != 'historyViewer')) && ((v != 'historyScroll')) && ((v != 'vimToggle')) && ((v != 'easymdeToggle')) && ((v != 'renderedToggle')) && ((v != 'easymdeContainer')) && ((v != 'richEditor')) && ((v != 'renderedContent')) && ((v != 'codeViewer')) && ((v != 'commentsPanel')) && ((v != 'commentDraftForm')) && ((v != 'workspacePanel')) && ((v != 'workspaceTerminal')) && ((v != 'workspaceShellSelect')) && ((v != 'workspaceShellForm')) && ((v != 'workspaceFullscreenToggle')) && ((v != 'workspacePanelDivider')) && ((v != 'workspacePanelToggle'))) throw new Error('Invalid EditorElementId `' + JSON.stringify(x) + '`');
  return x as EditorElementId;
}

export type EditorPushEvent = "setCode" | "setBlame" | "clearBlame" | "setComments" | "openSidebar" | "scrollTo" | "toggleBlame" | "setEditorMode" | "toggleSidebar" | "permalink" | "selectCommits" | "clearEditor" | "saveFile" | "fileSaved" | "commitChanges" | "commitSuccess" | "lspReady" | "lspStop" | "fetchHistoryContent" | "historyContent" | "startComment" | "cancelComment" | "saveComment" | "replyComment" | "editComment" | "cancelCommentEdit" | "updateComment" | "deleteLineComment" | "selectWorkspaceShell" | "terminateWorkspace" | "toggleWorkspacePanel" | "workspaceFileChanged"

export const validEditorPushEvent = (x: unknown): EditorPushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'setCode')) && ((v != 'setBlame')) && ((v != 'clearBlame')) && ((v != 'setComments')) && ((v != 'openSidebar')) && ((v != 'scrollTo')) && ((v != 'toggleBlame')) && ((v != 'setEditorMode')) && ((v != 'toggleSidebar')) && ((v != 'permalink')) && ((v != 'selectCommits')) && ((v != 'clearEditor')) && ((v != 'saveFile')) && ((v != 'fileSaved')) && ((v != 'commitChanges')) && ((v != 'commitSuccess')) && ((v != 'lspReady')) && ((v != 'lspStop')) && ((v != 'fetchHistoryContent')) && ((v != 'historyContent')) && ((v != 'startComment')) && ((v != 'cancelComment')) && ((v != 'saveComment')) && ((v != 'replyComment')) && ((v != 'editComment')) && ((v != 'cancelCommentEdit')) && ((v != 'updateComment')) && ((v != 'deleteLineComment')) && ((v != 'selectWorkspaceShell')) && ((v != 'terminateWorkspace')) && ((v != 'toggleWorkspacePanel')) && ((v != 'workspaceFileChanged'))) throw new Error('Invalid EditorPushEvent `' + JSON.stringify(x) + '`');
  return x as EditorPushEvent;
}

export interface BlameBlock {
  hash: CommitSha;
  author: string;
  authorMail: string;
  authorTime: BigNatural;
  summary: string;
  filename: FilePath;
  lineStart: LineNumber;
  lineEnd: LineNumber;
}

export const validBlameBlock = (input: unknown): BlameBlock => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["hash"] === undefined) throw new Error('`_x["hash"]` required but not present');
  
  if (_x["author"] === undefined) throw new Error('`_x["author"]` required but not present');
  if (typeof _x["author"] != 'string') throw new Error('`_x["author"]` must be a string');
  if (_x["authorMail"] === undefined) throw new Error('`_x["authorMail"]` required but not present');
  if (typeof _x["authorMail"] != 'string') throw new Error('`_x["authorMail"]` must be a string');
  if (_x["authorTime"] === undefined) throw new Error('`_x["authorTime"]` required but not present');
  validBigNatural(_x["authorTime"] as any);
  if (_x["summary"] === undefined) throw new Error('`_x["summary"]` required but not present');
  if (typeof _x["summary"] != 'string') throw new Error('`_x["summary"]` must be a string');
  if (_x["filename"] === undefined) throw new Error('`_x["filename"]` required but not present');
  
  if (_x["lineStart"] === undefined) throw new Error('`_x["lineStart"]` required but not present');
  validPositive(unwrapPositive(unwrapLineNumber(_x["lineStart"] as any)) as any);
  if (_x["lineEnd"] === undefined) throw new Error('`_x["lineEnd"]` required but not present');
  validPositive(unwrapPositive(unwrapLineNumber(_x["lineEnd"] as any)) as any);
  const x = input as BlameBlock;
  return x;
}
export const isValidBlameBlock = (input: unknown): boolean => {
  try { validBlameBlock(input); return true; } catch { return false; }
}

export interface SetCodePayload {
  content: string;
  lang: string;
}

export const validSetCodePayload = (input: unknown): SetCodePayload => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["content"] === undefined) throw new Error('`_x["content"]` required but not present');
  if (typeof _x["content"] != 'string') throw new Error('`_x["content"]` must be a string');
  if (_x["lang"] === undefined) throw new Error('`_x["lang"]` required but not present');
  if (typeof _x["lang"] != 'string') throw new Error('`_x["lang"]` must be a string');
  const x = input as SetCodePayload;
  return x;
}
export const isValidSetCodePayload = (input: unknown): boolean => {
  try { validSetCodePayload(input); return true; } catch { return false; }
}

export interface WorkspaceFileChangedPayload {
  new: string;
  lang: string;
}

export const validWorkspaceFileChangedPayload = (input: unknown): WorkspaceFileChangedPayload => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["new"] === undefined) throw new Error('`_x["new"]` required but not present');
  if (typeof _x["new"] != 'string') throw new Error('`_x["new"]` must be a string');
  if (_x["lang"] === undefined) throw new Error('`_x["lang"]` required but not present');
  if (typeof _x["lang"] != 'string') throw new Error('`_x["lang"]` must be a string');
  const x = input as WorkspaceFileChangedPayload;
  return x;
}
export const isValidWorkspaceFileChangedPayload = (input: unknown): boolean => {
  try { validWorkspaceFileChangedPayload(input); return true; } catch { return false; }
}

export interface SetCommentsPayload {
  lines: LineNumber[];
}

export const validSetCommentsPayload = (input: unknown): SetCommentsPayload => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["lines"] === undefined) throw new Error('`_x["lines"]` required but not present');
    if(_x["lines"] instanceof Array){
    if((_x["lines"] as any)?.length){
      (_x["lines"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validPositive(unwrapPositive(unwrapLineNumber(y as any)) as any);
      });
    }else{
      _x["lines"] = _x["lines"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["lines"]` must be an Array');
  }
  const x = input as SetCommentsPayload;
  return x;
}
export const isValidSetCommentsPayload = (input: unknown): boolean => {
  try { validSetCommentsPayload(input); return true; } catch { return false; }
}

export type LocalStorageKey = "blameWidth" | "editorVimMode" | "sidebarW" | "jobDetailW" | "diffCommitPanelW" | "workspacePanelW"

export const validLocalStorageKey = (x: unknown): LocalStorageKey => {
  const v = x as [string, unknown] | string;
  if(((v != 'blameWidth')) && ((v != 'editorVimMode')) && ((v != 'sidebarW')) && ((v != 'jobDetailW')) && ((v != 'diffCommitPanelW')) && ((v != 'workspacePanelW'))) throw new Error('Invalid LocalStorageKey `' + JSON.stringify(x) + '`');
  return x as LocalStorageKey;
}

export type OmnisearchHook = "omniSearch" | "omniSearchTrigger"

export const validOmnisearchHook = (x: unknown): OmnisearchHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'omniSearch')) && ((v != 'omniSearchTrigger'))) throw new Error('Invalid OmnisearchHook `' + JSON.stringify(x) + '`');
  return x as OmnisearchHook;
}

export type OmnisearchElementId = "omnisearchTrigger" | "omnisearchModal" | "omnisearchTabs" | "omnisearchResults"

export const validOmnisearchElementId = (x: unknown): OmnisearchElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'omnisearchTrigger')) && ((v != 'omnisearchModal')) && ((v != 'omnisearchTabs')) && ((v != 'omnisearchResults'))) throw new Error('Invalid OmnisearchElementId `' + JSON.stringify(x) + '`');
  return x as OmnisearchElementId;
}

export type OmnisearchPushEvent = "omnisearchToggle" | "omnisearchNavigate" | "omnisearchClose"

export const validOmnisearchPushEvent = (x: unknown): OmnisearchPushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'omnisearchToggle')) && ((v != 'omnisearchNavigate')) && ((v != 'omnisearchClose'))) throw new Error('Invalid OmnisearchPushEvent `' + JSON.stringify(x) + '`');
  return x as OmnisearchPushEvent;
}

export type SearchPushEvent = "searchData" | "fileSuggestions" | "autocompleteFiles"

export const validSearchPushEvent = (x: unknown): SearchPushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'searchData')) && ((v != 'fileSuggestions')) && ((v != 'autocompleteFiles'))) throw new Error('Invalid SearchPushEvent `' + JSON.stringify(x) + '`');
  return x as SearchPushEvent;
}

export type OmnisearchCategory = "nav" | "repo" | "issue" | "pr" | "lane" | "user" | "group"

export const validOmnisearchCategory = (x: unknown): OmnisearchCategory => {
  const v = x as [string, unknown] | string;
  if(((v != 'nav')) && ((v != 'repo')) && ((v != 'issue')) && ((v != 'pr')) && ((v != 'lane')) && ((v != 'user')) && ((v != 'group'))) throw new Error('Invalid OmnisearchCategory `' + JSON.stringify(x) + '`');
  return x as OmnisearchCategory;
}

export type PrHook = "prTimeline" | "inlineCommentForm" | "diffLazyLoad"

export const validPrHook = (x: unknown): PrHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'prTimeline')) && ((v != 'inlineCommentForm')) && ((v != 'diffLazyLoad'))) throw new Error('Invalid PrHook `' + JSON.stringify(x) + '`');
  return x as PrHook;
}

export type PrElementId = "prTitle" | "prBody" | "prBodyEditor" | "editPrBodyEditor" | "editPrBody" | "prDiff" | "prTimeline" | "prTimelineInner" | "prTimelineLine" | "prCommentBody" | "replySubmit" | "editCommentSubmit"

export const validPrElementId = (x: unknown): PrElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'prTitle')) && ((v != 'prBody')) && ((v != 'prBodyEditor')) && ((v != 'editPrBodyEditor')) && ((v != 'editPrBody')) && ((v != 'prDiff')) && ((v != 'prTimeline')) && ((v != 'prTimelineInner')) && ((v != 'prTimelineLine')) && ((v != 'prCommentBody')) && ((v != 'replySubmit')) && ((v != 'editCommentSubmit'))) throw new Error('Invalid PrElementId `' + JSON.stringify(x) + '`');
  return x as PrElementId;
}

export type PrPushEvent = "focusNextUnresolved" | "startReply" | "startEditComment" | "saveInlineComment"

export const validPrPushEvent = (x: unknown): PrPushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'focusNextUnresolved')) && ((v != 'startReply')) && ((v != 'startEditComment')) && ((v != 'saveInlineComment'))) throw new Error('Invalid PrPushEvent `' + JSON.stringify(x) + '`');
  return x as PrPushEvent;
}

export type ProfileHook = "profileIdentity" | "profileAbout" | "projectStories" | "activityViz" | "engagement" | "contribTerrain" | "obsidianBg"

export const validProfileHook = (x: unknown): ProfileHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'profileIdentity')) && ((v != 'profileAbout')) && ((v != 'projectStories')) && ((v != 'activityViz')) && ((v != 'engagement')) && ((v != 'contribTerrain')) && ((v != 'obsidianBg'))) throw new Error('Invalid ProfileHook `' + JSON.stringify(x) + '`');
  return x as ProfileHook;
}

export type ProfileElementId = "dashboardContent" | "activityViz" | "avatarUploadModal"

export const validProfileElementId = (x: unknown): ProfileElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'dashboardContent')) && ((v != 'activityViz')) && ((v != 'avatarUploadModal'))) throw new Error('Invalid ProfileElementId `' + JSON.stringify(x) + '`');
  return x as ProfileElementId;
}

export type RepoElementId = "httpUrl" | "sshUrl" | "deleteSubgroupModal" | "moveGroupModal"

export const validRepoElementId = (x: unknown): RepoElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'httpUrl')) && ((v != 'sshUrl')) && ((v != 'deleteSubgroupModal')) && ((v != 'moveGroupModal'))) throw new Error('Invalid RepoElementId `' + JSON.stringify(x) + '`');
  return x as RepoElementId;
}

export type SshHook = "sshKeyPaste"

export const validSshHook = (x: unknown): SshHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'sshKeyPaste'))) throw new Error('Invalid SshHook `' + JSON.stringify(x) + '`');
  return x as SshHook;
}

export type SshElementId = "sshKeyForm" | "sshKeyPasteZone" | "sshKeyPublicKey" | "sshKeyName" | "sshKeyExpiresAt" | "keyFeedback" | "copyPreviewFp"

export const validSshElementId = (x: unknown): SshElementId => {
  const v = x as [string, unknown] | string;
  if(((v != 'sshKeyForm')) && ((v != 'sshKeyPasteZone')) && ((v != 'sshKeyPublicKey')) && ((v != 'sshKeyName')) && ((v != 'sshKeyExpiresAt')) && ((v != 'keyFeedback')) && ((v != 'copyPreviewFp'))) throw new Error('Invalid SshElementId `' + JSON.stringify(x) + '`');
  return x as SshElementId;
}

export type ThemeHook = "themeApply"

export const validThemeHook = (x: unknown): ThemeHook => {
  const v = x as [string, unknown] | string;
  if(((v != 'themeApply'))) throw new Error('Invalid ThemeHook `' + JSON.stringify(x) + '`');
  return x as ThemeHook;
}

export type ThemePushEvent = "applyTheme" | "applyFont" | "applyCustomPalette" | "clearCustomPalette"

export const validThemePushEvent = (x: unknown): ThemePushEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'applyTheme')) && ((v != 'applyFont')) && ((v != 'applyCustomPalette')) && ((v != 'clearCustomPalette'))) throw new Error('Invalid ThemePushEvent `' + JSON.stringify(x) + '`');
  return x as ThemePushEvent;
}

export const illuminateAuthHook = (__x: AuthHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "authError": {
  return "AuthError";
  }
  case "authCard": {
  return "AuthCard";
  }
  }
}

export const illuminateUiHook = (__x: UiHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "modal": {
  return "Modal";
  }
  case "toast": {
  return "Toast";
  }
  case "dropdown": {
  return "Dropdown";
  }
  case "clipboard": {
  return "Clipboard";
  }
  case "tableSortAnimate": {
  return "TableSortAnimate";
  }
  case "detailsFold": {
  return "DetailsFold";
  }
  case "keyboardShortcuts": {
  return "KeyboardShortcuts";
  }
  case "rowExpander": {
  return "RowExpander";
  }
  case "commitPanelToggle": {
  return "CommitPanelToggle";
  }
  case "fileExpander": {
  return "FileExpander";
  }
  case "deleteConfirmGate": {
  return "DeleteConfirmGate";
  }
  case "workspacesNavToggle": {
  return "WorkspacesNavToggle";
  }
  case "clientTableSort": {
  return "ClientTableSort";
  }
  case "clientListFilter": {
  return "ClientListFilter";
  }
  case "disclosureToggle": {
  return "DisclosureToggle";
  }
  case "sectionToggle": {
  return "SectionToggle";
  }
  case "profileDropdown": {
  return "ProfileDropdown";
  }
  case "branchSelect": {
  return "BranchSelect";
  }
  }
}

export const illuminateBranchSelectPushEvent = (__x: BranchSelectPushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "branchSelectToggle": {
  return "branch_select_toggle";
  }
  case "branchSelectData": {
  return "branch_select_data";
  }
  case "branchSelectChoose": {
  return "branch_select_choose";
  }
  case "branchLaneCreate": {
  return "branch_lane_create";
  }
  }
}

export const illuminateToastKind = (__x: ToastKind): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "info": {
  return "info";
  }
  case "warning": {
  return "warning";
  }
  case "error": {
  return "error";
  }
  }
}

export const illuminateChannelEvent = (__x: ChannelEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "logLine": {
  return "log:line";
  }
  case "logChunk": {
  return "log:chunk";
  }
  case "jobStatus": {
  return "job:status";
  }
  }
}

export const illuminateDomEvent = (__x: DomEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "phxCopy": {
  return "phx:copy";
  }
  case "phxSetViewTab": {
  return "phx:set-view-tab";
  }
  case "phxToast": {
  return "phx:toast";
  }
  case "phxServerDraining": {
  return "phx:server-draining";
  }
  case "phxSaveFile": {
  return "phx:save-file";
  }
  case "phxEditorDocument": {
  return "phx:editor-document";
  }
  }
}

export const illuminateCookieKey = (__x: CookieKey): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "viewTab": {
  return "view_tab";
  }
  }
}

export const illuminateModalCloseEvent = (__x: ModalCloseEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "cancelAvatar": {
  return "cancel_avatar";
  }
  case "cancelEditing": {
  return "cancel_editing";
  }
  case "closeAttachEnvHostModal": {
  return "close_attach_env_host_modal";
  }
  case "closeAttachRunnerModal": {
  return "close_attach_runner_modal";
  }
  case "closeDeleteSubgroupModal": {
  return "close_delete_subgroup_modal";
  }
  case "closeGroupDetailsModal": {
  return "close_group_details_modal";
  }
  case "closeInviteModal": {
  return "close_invite_modal";
  }
  case "closeMoveGroupModal": {
  return "close_move_group_modal";
  }
  case "closeMoveRepoModal": {
  return "close_move_repo_modal";
  }
  case "closeMoveReposModal": {
  return "close_move_repos_modal";
  }
  case "closeNewGroupModal": {
  return "close_new_group_modal";
  }
  case "closeNewLaneModal": {
  return "close_new_lane_modal";
  }
  case "closeNewPrModal": {
  return "close_new_pr_modal";
  }
  case "closeNewRepoModal": {
  return "close_new_repo_modal";
  }
  case "closePbModal": {
  return "close_pb_modal";
  }
  case "closePtModal": {
  return "close_pt_modal";
  }
  case "closeSubgroupModal": {
  return "close_subgroup_modal";
  }
  case "closeWorkspacePickerModal": {
  return "close_workspace_picker_modal";
  }
  case "toggleClone": {
  return "toggle_clone";
  }
  case "toggleFork": {
  return "toggle_fork";
  }
  }
}

export const illuminateSortDir = (__x: SortDir): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "asc": {
  return "asc";
  }
  case "desc": {
  return "desc";
  }
  }
}

export const illuminateCiHook = (__x: CiHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "dagGraph": {
  return "DagGraph";
  }
  case "buildLog": {
  return "BuildLog";
  }
  case "evalLog": {
  return "EvalLog";
  }
  case "sparkline": {
  return "Sparkline";
  }
  case "ciAnimations": {
  return "CiAnimations";
  }
  case "kdeChart": {
  return "KdeChart";
  }
  case "scatterPlot": {
  return "ScatterPlot";
  }
  case "jobDetailPanel": {
  return "JobDetailPanel";
  }
  case "pipelineFavicon": {
  return "PipelineFavicon";
  }
  case "jobMetricsChart": {
  return "JobMetricsChart";
  }
  case "runnerMetricsChart": {
  return "RunnerMetricsChart";
  }
  case "sessionMetricsChart": {
  return "SessionMetricsChart";
  }
  case "liveDuration": {
  return "LiveDuration";
  }
  case "recentCommits": {
  return "RecentCommits";
  }
  case "commitSelect": {
  return "CommitSelect";
  }
  }
}

export const illuminateSurface = (__x: Surface): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "canvas": {
  return "canvas";
  }
  case "svg": {
  return "svg";
  }
  }
}

export const illuminateDataAttr = (__x: DataAttr): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "jobId": {
  return "jobId";
  }
  case "runnerId": {
  return "runnerId";
  }
  case "sessionId": {
  return "sessionId";
  }
  case "cores": {
  return "cores";
  }
  case "ramBytes": {
  return "ramBytes";
  }
  case "initialSamples": {
  return "initialSamples";
  }
  case "statusFilter": {
  return "statusFilter";
  }
  case "chartType": {
  return "type";
  }
  case "color": {
  return "color";
  }
  case "unit": {
  return "unit";
  }
  case "eventKey": {
  return "eventKey";
  }
  case "estimatedMs": {
  return "estimatedMs";
  }
  case "buildStartedAt": {
  return "buildStartedAt";
  }
  case "streaming": {
  return "streaming";
  }
  case "allHashes": {
  return "allHashes";
  }
  case "selected": {
  return "selected";
  }
  case "historyWaypoint": {
  return "historyWaypoint";
  }
  case "rowSelector": {
  return "rowSelector";
  }
  case "armRootSelector": {
  return "armRootSelector";
  }
  case "armChangeSelector": {
  return "armChangeSelector";
  }
  case "editorMode": {
  return "editorMode";
  }
  }
}

export const illuminateCiPushEvent = (__x: CiPushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "sparklineUpdate": {
  return "sparkline:update";
  }
  case "graphUpdate": {
  return "graph:update";
  }
  case "graphState": {
  return "graph:state";
  }
  case "buildLogLine": {
  return "build_log:line";
  }
  case "buildLogClear": {
  return "build_log:clear";
  }
  case "buildLogLoad": {
  return "build_log:load";
  }
  case "builderStarted": {
  return "builder_started";
  }
  case "jobSelect": {
  return "job:select";
  }
  case "jobDeselect": {
  return "job:deselect";
  }
  case "attemptSelect": {
  return "attempt:select";
  }
  case "jobMetricsSample": {
  return "job_metrics:sample";
  }
  case "jobMetricsLoad": {
  return "job_metrics:load";
  }
  case "runnerMetricsSample": {
  return "runner_metrics:sample";
  }
  case "runnerMetricsLoad": {
  return "runner_metrics:load";
  }
  case "sessionMetricsSample": {
  return "session_metrics:sample";
  }
  case "evalLogLine": {
  return "eval_log_line";
  }
  case "evalLogClear": {
  return "eval_log_clear";
  }
  }
}

export const illuminateCssClassDiff = (__x: CssClassDiff): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "diffCommentBtn": {
  return "diff-comment-btn";
  }
  case "diffCommentBtnReply": {
  return "diff-comment-btn-reply";
  }
  case "diffInlineWrap": {
  return "diff-inline-wrap";
  }
  case "diffInlineCell": {
  return "diff-inline-cell";
  }
  case "diffInlineFormClient": {
  return "diff-inline-form-client";
  }
  case "inlineCommentForm": {
  return "inline-comment-form";
  }
  }
}

export const illuminateCssClassCodeBrowser = (__x: CssClassCodeBrowser): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "blameActive": {
  return "blame-active";
  }
  case "blameDivider": {
  return "blame-divider";
  }
  case "cmBlameGutter": {
  return "cm-blame-gutter";
  }
  case "cmBlameAnnotation": {
  return "cm-blame-annotation";
  }
  case "cmCommentedLine": {
  return "cm-commented-line";
  }
  case "cmHighlightedLine": {
  return "cm-highlighted-line";
  }
  case "codeSidebar": {
  return "code-sidebar";
  }
  case "codeEditorLayout": {
  return "code-editor-layout";
  }
  case "dragging": {
  return "dragging";
  }
  case "resizing": {
  return "resizing";
  }
  case "sidebarHidden": {
  return "sidebar-hidden";
  }
  case "sidebarCloseBtn": {
  return "sidebar-close-btn";
  }
  case "workspaceTerminalFullscreen": {
  return "workspace-terminal-fullscreen";
  }
  }
}

export const illuminateCssClassModal = (__x: CssClassModal): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "modalPanel": {
  return "modal-panel";
  }
  case "modalBackdrop": {
  return "modal-backdrop";
  }
  case "awaitingGsap": {
  return "awaiting-gsap";
  }
  case "fadeOut": {
  return "fade-out";
  }
  case "appShell": {
  return "app-shell";
  }
  }
}

export const illuminateCssClassProfile = (__x: CssClassProfile): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "profileAvatar": {
  return "profile-avatar";
  }
  case "profileName": {
  return "profile-name";
  }
  case "profileMeta": {
  return "profile-meta";
  }
  case "profileSocials": {
  return "profile-socials";
  }
  }
}

export const illuminateEditorMode = (__x: EditorMode): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "source": {
  return "Source";
  }
  case "vim": {
  return "Vim";
  }
  case "rich": {
  return "Rich";
  }
  case "rendered": {
  return "Rendered";
  }
  }
}

export const illuminateEditorHook = (__x: EditorHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "codeEditor": {
  return "CodeEditor";
  }
  case "historyViewer": {
  return "HistoryViewer";
  }
  case "readmeHighlight": {
  return "ReadmeHighlight";
  }
  case "diffHighlight": {
  return "DiffHighlight";
  }
  case "diffInlineWrap": {
  return "DiffInlineWrap";
  }
  case "diffCommentBtn": {
  return "DiffCommentBtn";
  }
  case "codeRefAutocomplete": {
  return "CodeRefAutocomplete";
  }
  case "markdownEditor": {
  return "MarkdownEditor";
  }
  case "vimToggle": {
  return "VimToggle";
  }
  case "editorModeButton": {
  return "EditorModeButton";
  }
  case "paneDivider": {
  return "PaneDivider";
  }
  case "scrollPreserve": {
  return "ScrollPreserve";
  }
  case "workspaceTerminal": {
  return "WorkspaceTerminal";
  }
  case "commentAnchor": {
  return "CommentAnchor";
  }
  }
}

export const illuminateEditorPushEvent = (__x: EditorPushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "setCode": {
  return "set_code";
  }
  case "setBlame": {
  return "set_blame";
  }
  case "clearBlame": {
  return "clear_blame";
  }
  case "setComments": {
  return "set_comments";
  }
  case "openSidebar": {
  return "open_sidebar";
  }
  case "scrollTo": {
  return "scroll_to";
  }
  case "toggleBlame": {
  return "toggle_blame";
  }
  case "setEditorMode": {
  return "set_editor_mode";
  }
  case "toggleSidebar": {
  return "toggle_sidebar";
  }
  case "permalink": {
  return "permalink";
  }
  case "selectCommits": {
  return "select_commits";
  }
  case "clearEditor": {
  return "clear_editor";
  }
  case "saveFile": {
  return "save_file";
  }
  case "fileSaved": {
  return "file_saved";
  }
  case "commitChanges": {
  return "commit_changes";
  }
  case "commitSuccess": {
  return "commit_success";
  }
  case "lspReady": {
  return "lsp_ready";
  }
  case "lspStop": {
  return "lsp_stop";
  }
  case "fetchHistoryContent": {
  return "fetch_history_content";
  }
  case "historyContent": {
  return "history_content";
  }
  case "startComment": {
  return "start_comment";
  }
  case "cancelComment": {
  return "cancel_comment";
  }
  case "saveComment": {
  return "save_comment";
  }
  case "replyComment": {
  return "reply_comment";
  }
  case "editComment": {
  return "edit_comment";
  }
  case "cancelCommentEdit": {
  return "cancel_comment_edit";
  }
  case "updateComment": {
  return "update_comment";
  }
  case "deleteLineComment": {
  return "delete_line_comment";
  }
  case "selectWorkspaceShell": {
  return "select_workspace_shell";
  }
  case "terminateWorkspace": {
  return "terminate_workspace";
  }
  case "toggleWorkspacePanel": {
  return "toggle_workspace_panel";
  }
  case "workspaceFileChanged": {
  return "workspace_file_changed";
  }
  }
}

export const illuminateLocalStorageKey = (__x: LocalStorageKey): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "blameWidth": {
  return "blame-width";
  }
  case "editorVimMode": {
  return "editor-vim-mode";
  }
  case "sidebarW": {
  return "sidebar_w";
  }
  case "jobDetailW": {
  return "job_detail_w";
  }
  case "diffCommitPanelW": {
  return "diff_commit_panel_w";
  }
  case "workspacePanelW": {
  return "workspace_panel_w";
  }
  }
}

export const illuminateOmnisearchHook = (__x: OmnisearchHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "omniSearch": {
  return "OmniSearch";
  }
  case "omniSearchTrigger": {
  return "OmniSearchTrigger";
  }
  }
}

export const illuminateOmnisearchPushEvent = (__x: OmnisearchPushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "omnisearchToggle": {
  return "omnisearch_toggle";
  }
  case "omnisearchNavigate": {
  return "omnisearch_navigate";
  }
  case "omnisearchClose": {
  return "omnisearch_close";
  }
  }
}

export const illuminateSearchPushEvent = (__x: SearchPushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "searchData": {
  return "search_data";
  }
  case "fileSuggestions": {
  return "file_suggestions";
  }
  case "autocompleteFiles": {
  return "autocomplete_files";
  }
  }
}

export const illuminatePrHook = (__x: PrHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "prTimeline": {
  return "PrTimeline";
  }
  case "inlineCommentForm": {
  return "InlineCommentForm";
  }
  case "diffLazyLoad": {
  return "DiffLazyLoad";
  }
  }
}

export const illuminatePrPushEvent = (__x: PrPushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "focusNextUnresolved": {
  return "focus_next_unresolved";
  }
  case "startReply": {
  return "start_reply";
  }
  case "startEditComment": {
  return "start_edit_comment";
  }
  case "saveInlineComment": {
  return "save_inline_comment";
  }
  }
}

export const illuminateProfileHook = (__x: ProfileHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "profileIdentity": {
  return "ProfileIdentity";
  }
  case "profileAbout": {
  return "ProfileAbout";
  }
  case "projectStories": {
  return "ProjectStories";
  }
  case "activityViz": {
  return "ActivityViz";
  }
  case "engagement": {
  return "Engagement";
  }
  case "contribTerrain": {
  return "ContribTerrain";
  }
  case "obsidianBg": {
  return "ObsidianBg";
  }
  }
}

export const illuminateSshHook = (__x: SshHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "sshKeyPaste": {
  return "SshKeyPaste";
  }
  }
}

export const illuminateThemeHook = (__x: ThemeHook): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "themeApply": {
  return "ThemeApply";
  }
  }
}

export const illuminateThemePushEvent = (__x: ThemePushEvent): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "applyTheme": {
  return "apply_theme";
  }
  case "applyFont": {
  return "apply_font";
  }
  case "applyCustomPalette": {
  return "apply_custom_palette";
  }
  case "clearCustomPalette": {
  return "clear_custom_palette";
  }
  }
}

export const allDataAttrs: Set<DataAttr> =
  new Set(['jobId', 'runnerId', 'sessionId', 'cores', 'ramBytes', 'initialSamples', 'statusFilter', 'chartType', 'color', 'unit', 'eventKey', 'estimatedMs', 'buildStartedAt', 'streaming', 'allHashes', 'selected', 'historyWaypoint', 'rowSelector', 'armRootSelector', 'armChangeSelector', 'editorMode'])


export const jobMetricsChartDomContract: CiHookDomContract =
  {
    'hook': 'jobMetricsChart',
    'surface': 'canvas',
    'requiredDataAttrs': ['jobId', 'cores', 'ramBytes', 'initialSamples'],
  }


export const runnerMetricsChartDomContract: CiHookDomContract =
  {
    'hook': 'runnerMetricsChart',
    'surface': 'canvas',
    'requiredDataAttrs': ['runnerId', 'cores', 'ramBytes'],
  }


export const sessionMetricsChartDomContract: CiHookDomContract =
  {
    'hook': 'sessionMetricsChart',
    'surface': 'canvas',
    'requiredDataAttrs': ['sessionId', 'cores', 'ramBytes', 'initialSamples'],
  }


export const ciHookDomContracts: CiHookDomContract[] =
  [jobMetricsChartDomContract, runnerMetricsChartDomContract, sessionMetricsChartDomContract]


export const allOmnisearchCategories: Set<OmnisearchCategory> =
  new Set(['nav', 'repo', 'issue', 'pr', 'lane', 'user', 'group'])

//# sourceMappingURL=ui.ts.map
